import { AlertStyled } from "@/components/AlertStyled";
import { searchViewState } from "@/context/states";
import { GENDER_FILTER_OPTIONS } from "@/lib/StringConstants";
import { basierCirleBold } from "@/styles/fonts";
import theme from "@/styles/theme";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Button, Fade, MenuItem } from "@mui/material";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { GenreSelector } from "../GenreSelector";
import LanguageSelector from "../LanguageSelector";
import { RegionSelector } from "../RegionSelector";
import SelectStyled from "../SelectStyled";
import { SurroundingSearchFilter } from "../SurroundingSearchFilter";
import TextFieldStyled from "../TextFieldStyled";
import { AverageAgeFilter } from "./AverageAgeFilter";
import { NumberOfMusiciansFilter } from "./NumberOfMusiciansFilter";

export const MusikSuchenFilterContainer = ({
  ref,
  restoredFilter,
  filterMobile = false,
  onSearchChanged,
  onCancel,
  ...props
}) => {
  const router = useRouter();
  const [searchConfig, setSearchConfig] = useState(restoredFilter);
  const [filterOpen, setFilterOpen] = useState(false);
  const searchView = useRecoilValue(searchViewState);
  const [isFiltersExpanded, setFiltersExpanded] = useState(true);

  let queryString = "";
  let timerId = undefined;

  useEffect(() => {
    if (searchView === 0) {
      setFiltersExpanded(true);
    } else {
      setFiltersExpanded(false);
    }
  }, [searchView]);

  const handleQueryChanged = (event) => {
    queryString = event.target.value;

    if (timerId) {
      clearTimeout(timerId);
    }

    timerId = setTimeout(() => {
      if (
        queryString === event.target.value &&
        searchConfig.query !== queryString
      ) {
        handleSearchChanged(event);
      }
    }, 250);
  };

  const handleSearchChanged = (event, originalValue) => {
    const { name, value } = event.target;

    const newFilter = { ...searchConfig };
    newFilter[name] = originalValue ?? value;

    if (value === undefined || value === "undefined") {
      delete newFilter[name];
    }

    setSearchConfig(newFilter);

    if (onSearchChanged) {
      onSearchChanged(newFilter);
    }
  };

  const handleAgeFilterChanged = (values) => {
    const newFilter = { ...searchConfig };
    newFilter["age_max"] = new Date().getFullYear() - values[0];
    newFilter["age_min"] = new Date().getFullYear() - values[1];
    setSearchConfig(newFilter);

    if (onSearchChanged) {
      onSearchChanged(newFilter);
    }
  };

  const sort = [
    {
      value: "sort_this_month",
      label: "Diesen Monat angesagt",
    },
    {
      value: "sort_today",
      label: "Heute angesagt",
    },
    {
      value: "sort_favorites",
      label: "All Time Favorites",
    },
    {
      value: "sort_new",
      label: "Neu",
    },
  ];

  const style = {
    "& .showOnFilterOpened": {
      [theme.breakpoints.down("sm")]: {
        display: filterOpen ? "block" : "none",
      },
    },
  };

  return (
    <Box sx={style}>
      <Box className="showOnFilterOpened">
        <TextFieldStyled
          label="Suche"
          placeholder="z.B. Band-, Songname oder Genre"
          name="query"
          defaultValue={restoredFilter?.query}
          onChange={handleQueryChanged}
        />
        <SelectStyled
          name="_sort"
          label="Sortieren nach"
          values={sort}
          value={restoredFilter?._sort}
          onChange={handleSearchChanged}
          allowEmpty={true}
        >
          {sort.map((option, key) => (
            <MenuItem value={option} key={key}>
              {option.label}
            </MenuItem>
          ))}
        </SelectStyled>
      </Box>
      <GenreSelector
        variant="light"
        label="Genre"
        placeholder="Alle Genres"
        defaultValue={router.query?.genre ?? restoredFilter?.genre?.id}
        onChange={handleSearchChanged}
      />
      {searchView !== 2 && (
        <Box className="showOnFilterOpened">
          <SurroundingSearchFilter
            zipcode={searchConfig?.zipcode ?? restoredFilter?.zipcode}
            onChange={handleSearchChanged}
            disabled={searchView === 2}
          />
        </Box>
      )}
      <RegionSelector
        variant="light"
        label="Region"
        placeholder="Alle Regionen"
        value={router.query?.region ?? restoredFilter?.region?.id}
        onChange={handleSearchChanged}
      />

      {searchView === 1 && (
        <>
          <AverageAgeFilter
            onChange={handleAgeFilterChanged}
            min={searchConfig?.age_min ?? restoredFilter?.age_min ?? 0}
            max={searchConfig?.age_max ?? restoredFilter?.age_max ?? 99}
            disabled={searchView === 2}
          />
          <NumberOfMusiciansFilter
            onChange={handleSearchChanged}
            min={searchConfig?.members_min ?? restoredFilter?.members_min}
            max={searchConfig?.members_max ?? restoredFilter?.members_max}
            disabled={searchView === 2}
          />
          <SelectStyled
            name="gender"
            label="Gender der Musiker:innen"
            values={GENDER_FILTER_OPTIONS}
            value={searchConfig?.gender ?? restoredFilter?.gender}
            onChange={handleSearchChanged}
            tooltip="FINTA ist eine Abkürzung und steht für Frauen, Inter Menschen, Nichtbinäre Menschen, Trans Menschen und Agender Menschen"
            disabled={searchView === 2}
          >
            {GENDER_FILTER_OPTIONS.map((option, key) => (
              <MenuItem value={option} key={key}>
                {option.label}
              </MenuItem>
            ))}
          </SelectStyled>
        </>
      )}

      <Box
        className="showOnFilterOpened"
        sx={inactiveFilterStyles(isFiltersExpanded, searchView)}
      >
        <ButtonBase
          disableRipple
          onClick={() => setFiltersExpanded(!isFiltersExpanded)}
          className="toggle-inactive-filter-button"
        >
          <Grid container justifyContent="space-between">
            <Grid item xs>
              <Typography className="inactive-filter-title">
                Inaktive Filter
              </Typography>
            </Grid>
            <Grid item xs="auto">
              <Grid container>
                <Grid item xs="auto">
                  <Box className="filter-counter">
                    {searchView === 1 ? "1" : "5"}
                  </Box>
                </Grid>
                <Grid item xs="auto">
                  <ExpandMoreIcon />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ButtonBase>

        <Collapse in={isFiltersExpanded}>
          <Box className="inactive-filter-container">
            <Box className="filter-hint">
              <AlertStyled
                severity="warning"
                text="Diese Filter werden bei der aktuellen Suche nicht berücksichtigt."
              />
            </Box>
            {searchView === 2 && (
              <SurroundingSearchFilter
                zipcode={searchConfig?.zipcode ?? restoredFilter?.zipcode}
                onChange={handleSearchChanged}
                disabled={searchView === 2}
              />
            )}
            {searchView !== 1 && (
              <>
                <AverageAgeFilter
                  onChange={handleAgeFilterChanged}
                  min={searchConfig?.age_min ?? restoredFilter?.age_min ?? 0}
                  max={searchConfig?.age_max ?? restoredFilter?.age_max ?? 99}
                  disabled={searchView === 2}
                />
                <NumberOfMusiciansFilter
                  onChange={handleSearchChanged}
                  min={searchConfig?.members_min ?? restoredFilter?.members_min}
                  max={searchConfig?.members_max ?? restoredFilter?.members_max}
                  disabled={searchView === 2}
                />
                <SelectStyled
                  name="gender"
                  label="Gender der Musiker:innen"
                  values={GENDER_FILTER_OPTIONS}
                  value={restoredFilter?.gender}
                  onChange={handleSearchChanged}
                  tooltip="FINTA ist eine Abkürzung und steht für Frauen, Inter Menschen, Nichtbinäre Menschen, Trans Menschen und Agender Menschen"
                  disabled={searchView === 2}
                >
                  {GENDER_FILTER_OPTIONS.map((option, key) => (
                    <MenuItem value={option} key={key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SelectStyled>
              </>
            )}
            <LanguageSelector
              name="language"
              label="Songsprache"
              value={restoredFilter?.language}
              onChange={handleSearchChanged}
              disabled={searchView !== 0}
            />
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        {filterOpen ? (
          <Fade in={filterOpen}>
            <Box ref={ref} {...props}>
              <Button
                fullWidth
                color="secondary"
                variant="contained"
                startIcon={<ExpandLessIcon />}
                onClick={() => setFilterOpen(false)}
                sx={{ mt: 1 }}
              >
                weniger Filter anzeigen
              </Button>
            </Box>
          </Fade>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            startIcon={<FilterAltIcon />}
            onClick={() => setFilterOpen(true)}
            sx={{ mt: 1 }}
          >
            Mehr Filter anzeigen
          </Button>
        )}
      </Box>
    </Box>
  );
};

const inactiveFilterStyles = (isFiltersExpanded, searchView) => ({
  "& .toggle-inactive-filter-button": {
    display: searchView === 0 ? "none" : "block",
    width: "100%",
    margin: "0.5rem 0",
  },

  "& .inactive-filter-container": {
    display: isFiltersExpanded ? "block" : "none",
  },

  "& .filter-hint": {
    display: searchView === 1 || searchView === 2 ? "block" : "none",
  },

  "& .inactive-filter-title": {
    fontWeight: basierCirleBold.style.fontWeight,
    fontFamily: basierCirleBold.style.fontFamily,
    textTransform: "uppercase",
    textAlign: "left",
  },

  "& .filter-counter": {
    backgroundColor: grey[300],
    width: "1.5rem",
    height: "1.5rem",
    borderRadius: "0.25rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "0.875rem",
    color: theme.palette.common.black,
  },
});
